import marketplaceContractAbi from "../../contracts/marketplaceContractAbi.json";
import erc20Abi from "../../contracts/erc20Abi.json";
import { ethers } from "ethers";
import Config from "../../api/Config";

const MARKETPLACE_ADMIN_ROLE = ethers.utils.id("MARKETPLACE_ADMIN_ROLE");

const state = {
  calcAddress: null,
  calcContract: null,
  calcSigner: null
};

const getters = {
  getMarketplaceAddress(state) {
    return state.calcAddress;
  },
  getMarketplaceContract(state) {
    return state.calcContract;
  }
};

function extractErrorMessage(e) {
  if (e && e.message && e.message.includes('user rejected')) return 'Transaction rejected in wallet.';
  if (e && e.message && e.message.includes('429')) return 'The network RPC node is currently busy (HTTP 429). Please try again in a few seconds.';
  if (e && e.message && e.message.includes('HTTP client error')) return 'The network RPC provider returned a connection error. Check your wallet activity.';
  return (e && (e.reason || (e.data && e.data.message) || e.message)) || 'Transaction failed';
}

const actions = {
  async fetchMarketplaceContract({ commit, rootState }) {
    const providerW3m = rootState.accounts.providerW3m;
    const provider = new ethers.providers.Web3Provider(providerW3m);
    const config = (await Config.getConfig()).data;
    const contractAddress = config.marketplace_contract_address;
    const signer = provider.getSigner();
    const contract = new ethers.Contract(contractAddress, marketplaceContractAbi, signer);

    commit("setMarketplaceContract", contract);
    commit("setMarketplaceContractAddress", contractAddress);
    commit("setSigner", signer);

    return contract;
  },

  async ensureAllowance({ state }, { tokenAddress, amountWei }) {
    const signer = state.calcSigner;
    const tokenContract = new ethers.Contract(tokenAddress, erc20Abi, signer);
    const owner = await signer.getAddress();
    const allowance = await tokenContract.allowance(owner, state.calcAddress);
    if (allowance.lt(amountWei)) {
      const tx = await tokenContract.approve(state.calcAddress, amountWei);
      await tx.wait();
    }
  },

  async createOffer({ state, dispatch }, { zco2AmountWei, paymentTokenAddress, pricePerTokenWei }) {
    try {
      const zco2Address = await state.calcContract.zco2Token();
      await dispatch("ensureAllowance", { tokenAddress: zco2Address, amountWei: zco2AmountWei });

      const tx = await state.calcContract.createOffer(zco2AmountWei, paymentTokenAddress, pricePerTokenWei);
      const receipt = await tx.wait();

      const parsed = receipt.logs
        .map((log) => { try { return state.calcContract.interface.parseLog(log); } catch (e) { return null; } })
        .find((p) => p && p.name === 'OfferCreated');

      return { tx, receipt, offerId: parsed ? parsed.args.offerId.toString() : null };
    } catch (e) {
      throw new Error(extractErrorMessage(e));
    }
  },

  async modifyOffer({ state, dispatch }, { offerId, newAmountWei, newPaymentTokenAddress, newPricePerTokenWei, isIncrease }) {
    try {
      if (isIncrease) {
        const zco2Address = await state.calcContract.zco2Token();
        await dispatch("ensureAllowance", { tokenAddress: zco2Address, amountWei: newAmountWei });
      }
      const tx = await state.calcContract.modifyOffer(offerId, newAmountWei, newPaymentTokenAddress, newPricePerTokenWei);
      await tx.wait();
      return tx;
    } catch (e) {
      throw new Error(extractErrorMessage(e));
    }
  },

  async cancelOffer({ state }, offerId) {
    try {
      const tx = await state.calcContract.cancelOffer(offerId);
      await tx.wait();
      return tx;
    } catch (e) {
      throw new Error(extractErrorMessage(e));
    }
  },

  async buyOffer({ state, dispatch }, { offerId, zco2AmountWei, paymentTokenAddress, paymentAmountWei }) {
    try {
      await dispatch("ensureAllowance", { tokenAddress: paymentTokenAddress, amountWei: paymentAmountWei });
      const tx = await state.calcContract.buy(offerId, zco2AmountWei);
      const receipt = await tx.wait();
      return { tx, receipt };
    } catch (e) {
      throw new Error(extractErrorMessage(e));
    }
  },

  async hasAdminRole({ state }, wallet) {
    if (!state.calcContract) return false;
    try {
      return await state.calcContract.hasRole(MARKETPLACE_ADMIN_ROLE, wallet);
    } catch (e) {
      return false;
    }
  },

  async setFees({ state }, { feeBps, flatFeeWei }) {
    try {
      const tx = await state.calcContract.setFees(feeBps, flatFeeWei);
      await tx.wait();
      return tx;
    } catch (e) {
      throw new Error(extractErrorMessage(e));
    }
  },

  async withdrawFees({ state }, { tokenAddress, to, amountRaw }) {
    try {
      const tx = await state.calcContract.withdrawFees(tokenAddress, to, amountRaw);
      await tx.wait();
      return tx;
    } catch (e) {
      throw new Error(extractErrorMessage(e));
    }
  },

  async getAccruedFees({ state }, tokenAddress) {
    return state.calcContract.getAccruedFees(tokenAddress);
  },

  async addPaymentToken({ state }, tokenAddress) {
    try {
      const tx = await state.calcContract.addPaymentToken(tokenAddress);
      await tx.wait();
      return tx;
    } catch (e) {
      throw new Error(extractErrorMessage(e));
    }
  },

  async removePaymentToken({ state }, tokenAddress) {
    try {
      const tx = await state.calcContract.removePaymentToken(tokenAddress);
      await tx.wait();
      return tx;
    } catch (e) {
      throw new Error(extractErrorMessage(e));
    }
  },

  async fetchTokenMetadata({ state }, tokenAddress) {
    const provider = state.calcContract.provider;
    const tokenContract = new ethers.Contract(tokenAddress, erc20Abi, provider);
    const [name, symbol, decimals] = await Promise.all([
      tokenContract.name(),
      tokenContract.symbol(),
      tokenContract.decimals()
    ]);
    return { name, symbol, decimals };
  },

  async isWhitelistEnabled({ state }) {
    return state.calcContract.whitelistEnabled();
  },

  async isWhitelistedSeller({ state }, wallet) {
    return state.calcContract.isWhitelistedSeller(wallet);
  },

  async setWhitelistEnabled({ state }, enabled) {
    try {
      const tx = await state.calcContract.setWhitelistEnabled(enabled);
      await tx.wait();
      return tx;
    } catch (e) {
      throw new Error(extractErrorMessage(e));
    }
  },

  async addToWhitelist({ state }, wallet) {
    try {
      const tx = await state.calcContract.addSellerToWhitelist(wallet);
      await tx.wait();
      return tx;
    } catch (e) {
      throw new Error(extractErrorMessage(e));
    }
  },

  async removeFromWhitelist({ state }, wallet) {
    try {
      const tx = await state.calcContract.removeSellerFromWhitelist(wallet);
      await tx.wait();
      return tx;
    } catch (e) {
      throw new Error(extractErrorMessage(e));
    }
  }
};

const mutations = {
  setMarketplaceContract(state, _contract) {
    state.calcContract = _contract;
  },
  setMarketplaceContractAddress(state, _contractAddress) {
    state.calcAddress = _contractAddress;
  },
  setSigner(state, _signer) {
    state.calcSigner = _signer;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
